/**
 * Dispatcher d'événements postMessage SDK → callbacks utilisateur (Phase 4).
 *
 * Reçoit les messages du protocole `embedEvents` émis par l'iframe widget,
 * filtre strictement (source === iframe.contentWindow + origin whitelist), et
 * route vers les callbacks fournis par le pro dans `init()`.
 *
 * RÉTROCOMPAT : ce dispatcher ne gère PAS le format legacy `rtt-widget-resize`
 * (objet plat sans `source/version`). Le helper `attachResizeListener` de
 * `resize.ts` continue d'écouter ce format en parallèle pour ne pas casser les
 * iframes legacy. À terme (v2 SDK) on pourra dropper le legacy.
 *
 * Robustesse : chaque callback est wrappé dans un try/catch — une exception
 * dans le callback du pro (ex. gtag pas chargé, fbq qui throw) ne doit JAMAIS
 * casser l'écoute des events suivants.
 */

import { isWidgetEvent, type RintintinWidgetEvent } from './events';
import type { RintintinBookingInitOptions } from './types';

const LOG_PREFIX = '[RintintinBooking]';

/** Sous-ensemble des callbacks routés par le dispatcher. */
export type DispatcherCallbacks = Pick<
  RintintinBookingInitOptions,
  | 'onReady'
  | 'onResize'
  | 'onBookingStarted'
  | 'onCartConfirmed'
  | 'onCheckoutComplete'
  | 'onForfaitClicked'
  | 'onCreditsClicked'
  | 'onError'
>;

/** Options du dispatcher. */
export interface CreateEventDispatcherOptions {
  /** Iframe à écouter (filtre `event.source === iframe.contentWindow`). */
  iframe: HTMLIFrameElement;
  /**
   * Liste d'origines autorisées. Si vide / `null`, on accepte tout (utile pour
   * les tests). En production le caller passe au moins `[baseUrl.origin]`.
   */
  allowedOrigins: string[] | null;
  /** Callbacks utilisateur (les undefined sont ignorés). */
  callbacks: DispatcherCallbacks;
}

/** Handle retourné par `createEventDispatcher` — `destroy()` retire le listener. */
export interface EventDispatcherHandle {
  destroy(): void;
}

/**
 * Lookup table type → callback. On accepte `any` pour le payload car l'union
 * de tous les payloads forcerait le caller à narrow à chaque event ; le
 * filtrage par `type` est suffisant pour garantir le bon shape.
 */
type CallbackKey = keyof DispatcherCallbacks;

const TYPE_TO_CALLBACK: Record<RintintinWidgetEvent['type'], CallbackKey> = {
  ready: 'onReady',
  resize: 'onResize',
  'booking-started': 'onBookingStarted',
  'cart-confirmed': 'onCartConfirmed',
  'checkout-complete': 'onCheckoutComplete',
  'forfait-clicked': 'onForfaitClicked',
  'credits-clicked': 'onCreditsClicked',
  error: 'onError',
};

/**
 * Crée et attache le listener de messages.
 * Retourne un handle avec `destroy()` pour cleanup au démontage de l'instance.
 */
export function createEventDispatcher(opts: CreateEventDispatcherOptions): EventDispatcherHandle {
  const { iframe, allowedOrigins, callbacks } = opts;

  const onMessage = (event: MessageEvent): void => {
    // 1. Filtre source : ne traite QUE les messages issus de notre iframe.
    if (event.source !== iframe.contentWindow) return;

    // 2. Filtre origine : whitelist stricte sauf si `null` (= mode permissif tests).
    if (allowedOrigins && allowedOrigins.length > 0) {
      if (!allowedOrigins.includes(event.origin)) return;
    }

    // 3. Validation structure (source/type/version/payload).
    if (!isWidgetEvent(event.data)) return;

    const data = event.data;
    const callbackKey = TYPE_TO_CALLBACK[data.type];
    if (!callbackKey) return;

    const cb = callbacks[callbackKey] as ((payload: unknown) => void) | undefined;
    if (!cb) return;

    // 4. Routing isolé : on protège l'écoute future des autres events si le
    // callback du pro throw (gtag pas dispo, fbq pas chargé, etc.).
    try {
      cb(data.payload);
    } catch (err) {
      console.error(`${LOG_PREFIX} callback ${callbackKey} a throw :`, err);
    }
  };

  window.addEventListener('message', onMessage);

  return {
    destroy() {
      window.removeEventListener('message', onMessage);
    },
  };
}

/**
 * Helper : extrait l'origine canonique d'une baseUrl. Retourne `null` si invalide.
 * Utilisé par les modes `inline` et `popup` pour calculer les `allowedOrigins`
 * par défaut quand le pro n'en fournit pas.
 */
export function defaultAllowedOriginsFromBase(baseUrl: string): string[] | null {
  try {
    return [new URL(baseUrl).origin];
  } catch {
    return null;
  }
}

/**
 * Construit la liste effective des origines à matcher. Si le pro fournit une
 * liste explicite (`opts.allowedOrigins`), on la prend telle quelle (en
 * filtrant les entrées vides). Sinon, on déduit de la baseUrl.
 */
export function resolveAllowedOrigins(
  optAllowed: string[] | undefined,
  baseUrl: string
): string[] | null {
  if (optAllowed && optAllowed.length > 0) {
    const cleaned = optAllowed.map(s => s.trim()).filter(Boolean);
    if (cleaned.length > 0) return cleaned;
  }
  return defaultAllowedOriginsFromBase(baseUrl);
}

/**
 * Petit utilitaire pour les sites qui veulent extraire les callbacks depuis les
 * options `init()` sans dupliquer les clés à chaque appel.
 */
export function pickDispatcherCallbacks(options: RintintinBookingInitOptions): DispatcherCallbacks {
  return {
    onReady: options.onReady,
    onResize: options.onResize,
    onBookingStarted: options.onBookingStarted,
    onCartConfirmed: options.onCartConfirmed,
    onCheckoutComplete: options.onCheckoutComplete,
    onForfaitClicked: options.onForfaitClicked,
    onCreditsClicked: options.onCreditsClicked,
    onError: options.onError,
  };
}
